import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, Trophy, AlertTriangle, Target, Flame, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api_getSession, api_getMyProfile } from "@/lib/api";

export default function Reflection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await api_getSession(id);
        setSession(s);
        const p = await api_getMyProfile();
        setProfile(p);
      } catch (e) {
        toast.error("Could not load reflection.");
      }
    })();
  }, [id]);

  if (!session || !profile) {
    return <AppShell><div className="text-center py-20 text-slate-500">Analyzing your session…</div></AppShell>;
  }

  const r = session.reflection || {};
  const totals = session.drills.reduce(
    (acc, d) => {
      const outs = session.feedback?.[d.id] || [];
      acc.total += outs.length;
      acc.success += outs.filter((o) => o === "success").length;
      acc.mixed += outs.filter((o) => o === "mixed").length;
      acc.failed += outs.filter((o) => o === "failed").length;
      return acc;
    },
    { total: 0, success: 0, mixed: 0, failed: 0 }
  );
  const successRate = totals.total ? Math.round((totals.success / totals.total) * 100) : 0;

  return (
    <AppShell profile={profile}>
      <button onClick={() => navigate("/home")} className="text-sm font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="reflection-back">
        <ArrowLeft className="w-4 h-4" /> Back to today
      </button>

      {/* Header */}
      <div className="bg-slate-900 text-white rounded-3xl p-8 mb-6 relative overflow-hidden">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">Session Reflection</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-black tracking-tight leading-tight">{session.title}</h1>
        <p className="mt-3 text-white/70 max-w-2xl leading-relaxed" data-testid="reflection-overall">{r.overall}</p>

        <div className="mt-8 grid grid-cols-3 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Success rate</p>
            <p className="mt-1 text-4xl font-heading font-black text-[#0B2545]" data-testid="reflection-rate">{successRate}%</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Shots logged</p>
            <p className="mt-1 text-4xl font-heading font-black">{totals.total}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Streak</p>
            <p className="mt-1 text-4xl font-heading font-black inline-flex items-center gap-2"><Flame className="w-7 h-7 text-[#0B2545]" /> {profile.streak_count}</p>
          </div>
        </div>
      </div>

      {/* Range to course translation */}
      <div className="bg-blue-50 border-2 border-[#0B2545]/30 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0B2545] text-white flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-600">Range → Course translation</p>
        </div>
        <p className="mt-4 text-xl sm:text-2xl font-heading font-bold leading-snug text-slate-900" data-testid="reflection-translation">
          {r.course_translation}
        </p>
      </div>

      {/* Wins + Leaks */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Trophy className="w-5 h-5" />
            </div>
            <h4 className="font-heading font-bold text-lg">Wins</h4>
          </div>
          <ul className="mt-4 space-y-2">
            {(r.wins || []).map((w, i) => (
              <li key={i} data-testid={`win-${i}`} className="text-slate-700 text-sm leading-relaxed flex gap-2"><span className="text-emerald-500 font-bold">✓</span>{w}</li>
            ))}
            {(!r.wins || r.wins.length === 0) && <li className="text-sm text-slate-500">No wins recorded.</li>}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h4 className="font-heading font-bold text-lg">Leaks</h4>
          </div>
          <ul className="mt-4 space-y-2">
            {(r.leaks || []).map((w, i) => (
              <li key={i} data-testid={`leak-${i}`} className="text-slate-700 text-sm leading-relaxed flex gap-2"><span className="text-amber-500 font-bold">!</span>{w}</li>
            ))}
            {(!r.leaks || r.leaks.length === 0) && <li className="text-sm text-slate-500">No leaks this session.</li>}
          </ul>
        </div>
      </div>

      {/* Next focus */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <Target className="w-5 h-5" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Next focus</p>
        </div>
        <p className="mt-3 text-lg font-semibold text-slate-900" data-testid="reflection-next">{r.next_focus}</p>
      </div>

      {/* Drill breakdown */}
      <h3 className="text-2xl font-heading font-black mt-8 mb-3">Drill breakdown</h3>
      <div className="space-y-3">
        {session.drills.map((d) => {
          const outs = session.feedback?.[d.id] || [];
          const ss = outs.filter((o) => o === "success").length;
          const mm = outs.filter((o) => o === "mixed").length;
          const ff = outs.filter((o) => o === "failed").length;
          const rate = outs.length ? Math.round((ss / outs.length) * 100) : 0;
          return (
            <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`breakdown-${d.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{d.skill.replace("_", " ")}</p>
                  <p className="mt-1 font-heading font-bold text-lg">{d.title}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hit rate</p>
                  <p className="font-heading font-black text-2xl">{rate}%</p>
                </div>
              </div>
              <div className="mt-3 flex gap-1">
                {Array.from({ length: d.shot_count }).map((_, i) => {
                  const o = outs[i];
                  const bg = !o ? "bg-slate-200" : o === "success" ? "bg-emerald-500" : o === "mixed" ? "bg-amber-500" : "bg-blue-500";
                  return <div key={i} className={`h-2 flex-1 rounded-full ${bg}`} />;
                })}
              </div>
              <div className="mt-3 flex gap-3 text-xs font-bold">
                <span className="text-emerald-700">✓ {ss}</span>
                <span className="text-amber-700">~ {mm}</span>
                <span className="text-red-700">✗ {ff}</span>
              </div>
              {session.notes?.[d.id] && (
                <p className="mt-3 text-sm text-slate-600 italic border-l-2 border-slate-200 pl-3">"{session.notes[d.id]}"</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={() => navigate("/home")} data-testid="reflection-done" className="btn-primary">
          Back to dashboard <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </AppShell>
  );
}
